import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'statux'

import Alert from '../../../../components/Alert/Alert'
import Image from '../../../../components/Image/Image'

import authProvider from '../../../../utils/auth-provider'
import { getCssVar } from '../../../../utils/css'
import { graphqlClient, gql } from '../../../../utils/gql'
import { getInitials } from '../../../../utils/strings'

export default function DashboardHomeNetwork (props = {
    profile: null
}) {
    const isProfileApproved = !authProvider.getDefaultRole()
        .match(/^(admin|ebpc_judge|user)$/)

    const isAdmin = authProvider.getDefaultRole() === 'admin'

    const isMember = (props.profile || {}).role === 'member'

    const userId = (authProvider.getIdentity() || {}).id

    const history = useHistory()

    const [pairs, setPairs] = useState(
        (history.location.state || {}).pairs
            || null)

    const [ebpcApplication, setEbpcApplication] = useState(
        (history.location.state || {}).ebpcApplication
            || null)

    const [advancedMembership, setAdvancedMembership] = useState(
        (history.location.state || {}).advancedMembership
            || null)

    const {
        advancedMembershipAt,
        advancedMembershipPrice,
        ebpcContest,
        ebpcApplicationsOpen,
        ebpcApplicationsEndDateStr
    } = useSelector()

    useEffect(async () => {
        if (isAdmin
                || !userId)
            return

        let queryReturnObj

        if (isMember)
            queryReturnObj = gql`
                advanced_membership
                member_pairs {
                    email
                    company
                    title
                    type
                    user {
                        id
                        display_name
                        avatar_url
                    }
                }
                ebpc_applications {
                    created_at
                    submitted_at
                    ebpc_contest_id
                }
        `

        if (!isMember)
            queryReturnObj = gql`
                advisor_pairs {
                    type
                    company
                    company_logo
                    company_website
                    user {
                        avatar_url
                        created_at
                        display_name
                        id
                    }
                }`

        const queryProfile = await graphqlClient
            .request(gql`
                query {
                    profiles (
                        limit: 1,
                        where: {
                            user: { id: { _eq: "${userId}" }}
                        }
                    ) {
                        ${queryReturnObj}
                    }
                }
            `, null, {
                headers: {
                    'x-hasura-role': 'me'
                }
            })
            .then(obj => (obj.profiles || [])[0])
            .catch((err) => {
                console.warn(err)
                return false
            })

        const isAdvancedMembership = Boolean(isMember
            && queryProfile.advanced_membership)

        const queryEbpcApplication = isMember
            ? ((queryProfile || {}).ebpc_applications || [])
                .filter(obj => obj.ebpc_contest_id === (ebpcContest || {}).id)[0]
                    || { created_at: null }
            : null

        if (JSON.stringify(ebpcApplication)
                !== JSON.stringify(queryEbpcApplication))
            setEbpcApplication(queryEbpcApplication)

        let queryPairs
            = ((queryProfile || {})[`${isMember ? 'member' : 'advisor'}_pairs`] || [])
                .sort((a, b) => {
                    const createdA = (new Date(a.user.created_at)).getTime()
                    const createdB = (new Date(b.user.created_at)).getTime()

                    if (createdA === createdB)
                        return 0

                    return createdA < createdB
                        ? -1
                        : 1
                })

        if (!isMember) {
            const slugify = (str) => str
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')

            const companies = queryPairs
                .reduce((acc, curr) => {
                    const slug = slugify(curr.company)

                    if (acc[slug]
                            && !acc[slug].company_website
                            && curr.company_website)
                        acc[slug].company_website = curr.company_website

                    if (acc[slug]
                            && !acc[slug].company_logo
                            && curr.company_logo)
                        acc[slug].company_logo = curr.company_logo

                    if (!acc[slug])
                        acc[slug] = {
                            company: curr.company,
                            company_logo: curr.company_logo,
                            company_profile: `/dashboard/profiles/${
                                curr.user.id}`,
                            company_website: curr.company_website,
                            members: [],
                            type: curr.type
                        }

                    acc[slug].members.push(curr.user)
                    return acc
                }, {})

            queryPairs = Object.keys(companies)
                .map(key => (companies[key]))
        }

        if (JSON.stringify(pairs)
                !== JSON.stringify(queryPairs))
            setPairs(queryPairs)

        if (advancedMembership
                !== isAdvancedMembership)
            setAdvancedMembership(isAdvancedMembership)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                ebpcApplication: queryEbpcApplication,
                pairs: queryPairs,
                advancedMembership: isAdvancedMembership
            }))
    }, [userId])

    if (isAdmin)
        return null

    const renderAdvisorPairs = () => (
        <ul className="dashboard-home-network-list dashboard-home-network-pairs-list dashboard-home-network-pairs-list-advisor">
            {pairs
                .map((obj, idx) => (
                    <li
                        className="dashboard-home-network-list-item row flex-nowrap"
                        key={`dashboard-home-network-pairs-list-${idx}`}
                    >
                        <div className="col-auto pad-0-r">
                            <div
                                className={`dashboard-home-network-company-logo${
                                    !obj.company_logo
                                        ? ' round ratio-square'
                                        : ''}`}
                                style={!obj.company_logo
                                    ? {
                                        background: getCssVar('--mystic'),
                                        color: getCssVar('--oxford')
                                    }
                                    : null}
                            >
                                {obj.company_logo
                                    && (
                                        <Image
                                            src={`${obj.company_logo}?w=140`}
                                            alt={`${obj.company || 'company'} logo`}
                                            data-object-fit="contain"
                                        />)}
                                {!obj.company_logo
                                    && (
                                        <span className="display-flex flex-justify-content-center flex-align-items-center">
                                            {getInitials(obj.company, 1)}
                                        </span>)}
                            </div>
                        </div>
                        <div className="col body-6 font-weight-book">
                            <Link
                                className="display-inline-block"
                                to={obj.company_profile}
                            >
                                <h3 className="body-4 font-weight-medium display display-inline-block">
                                    {obj.company}
                                </h3>
                            </Link>

                            {obj.company_website
                                && (
                                    <div className="display-flex">
                                        <a
                                            className="display-inline-block"
                                            href={obj.company_website}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            title="Opens in a new tab"
                                        >
                                            <span className="font-weight-book">
                                                {obj.company_website
                                                    .replace(/^.+:\/\/(www)*/, '')
                                                    .replace(/\/$/, '')}
                                            </span>
                                        </a>
                                    </div>)}
                            {Boolean(((obj || {}).members || []).length)
                                && (
                                    <ul
                                        className="dashboard-home-network-list-item-avatars row-condensed"
                                        aria-label="Team members"
                                    >
                                        {obj.members
                                            .map((_obj, _idx) => (
                                                <li
                                                    key={`dashboard-home-network-pairs-list-${
                                                        idx}-${_idx}`}
                                                    className="col-auto"
                                                >
                                                    <div
                                                        className="avatar-sm"
                                                        style={{
                                                            border: _obj.avatar_url
                                                                ? `1px solid ${getCssVar('--cadet')}`
                                                                : null,
                                                            background: !_obj.avatar_url
                                                                ? getCssVar('--mystic')
                                                                : null,
                                                            color: !_obj.avatar_url
                                                                ? getCssVar('--oxford')
                                                                : null
                                                        }}
                                                    >
                                                        {_obj.avatar_url
                                                            && (
                                                                <Image
                                                                    src={`${_obj.avatar_url}?w=96`}
                                                                    alt={`Avatar of ${_obj.display_name}`}
                                                                    data-object-fit="cover"
                                                                    loading="lazy"
                                                                />)}
                                                        {!_obj.avatar_url
                                                            && (
                                                                <span
                                                                    aria-label={obj.display_name}
                                                                >
                                                                    {getInitials(_obj.display_name)}
                                                                </span>)}
                                                    </div>
                                                </li>))}

                                    </ul>)}
                            <p>
                                <span
                                    className="display-flex margin-1-t pad-1-t"
                                    style={{
                                        color: 'var(--navy)',
                                        borderTop: '1px solid var(--geyser)'
                                    }}
                                >
                                    <svg
                                        className="icon-flag-outline margin-1-r"
                                        width="16"
                                        height="16"
                                    >
                                        <use xlinkHref="#icon-flag-outline" />
                                    </svg>
                                    <span>{obj.type}</span>
                                </span>
                            </p>
                        </div>
                    </li>))}
        </ul>
    )

    const renderMemberPairs = () => (
        <ul className="dashboard-home-network-list dashboard-home-network-pairs-list dashboard-home-network-pairs-list-member">
            {pairs
                .map((obj, idx) => (
                    <li
                        className="dashboard-home-network-list-item row flex-nowrap"
                        key={`dashboard-home-network-pairs-list-${idx}`}
                    >
                        <div className="col-auto pad-0-r">
                            <div
                                className="avatar avatar-lg"
                                style={obj.user.avatar_url
                                    ? null
                                    : {
                                        background: getCssVar('--mystic'),
                                        color: getCssVar('--oxford'),
                                        fontSize: 22
                                    }}
                            >
                                {obj.user.avatar_url
                                    && (
                                        <Image
                                            src={`${obj.user.avatar_url}?w=192`}
                                            alt={`Avatar of ${obj.user.display_name}`}
                                            data-object-fit="cover"
                                            loading="lazy"
                                        />)}
                                {!obj.user.avatar_url
                                    && (
                                        <span>
                                            {getInitials(obj.user.display_name)}
                                        </span>)}
                            </div>
                        </div>
                        <div className="col body-6 font-weight-book">
                            <Link
                                className="display-inline-block"
                                to={`/dashboard/profiles/${obj.user.id}`}
                            >
                                <h3 className="body-4 font-weight-medium display display-inline-block">
                                    {obj.user.display_name}
                                </h3>
                            </Link>
                            {(obj.title || obj.company)
                                && (
                                    <p>
                                        <span className="display-flex">
                                            <svg
                                                className="icon-work-outline margin-1-r"
                                                width="16"
                                                height="16"
                                            >
                                                <use xlinkHref="#icon-work-outline" />
                                            </svg>
                                            <span>
                                                {obj.title
                                                    && (
                                                        <span>
                                                            {obj.title}
                                                        </span>)}
                                                {obj.company
                                                    && (
                                                        <span>
                                                            {obj.title ? ', ' : ''}
                                                            {obj.company}
                                                        </span>)}
                                            </span>
                                        </span>
                                    </p>)}
                            <p>
                                <span className="display-flex">
                                    <a
                                        className="display-inline-flex"
                                        href="mailto:jgamba@upenn.edu"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        title="Opens in default email client"
                                    >
                                        <span className="flex-none margin-1-r">
                                            <svg
                                                className="icon-mail-outline"
                                                width="16"
                                                height="16"
                                            >
                                                <use xlinkHref="#icon-mail-outline" />
                                            </svg>
                                        </span>
                                        <span className="font-weight-normal">{obj.email}</span>
                                    </a>
                                </span>

                                <span
                                    className="display-flex margin-1-t pad-1-t"
                                    style={{
                                        color: 'var(--navy)',
                                        borderTop: '1px solid var(--geyser)'
                                    }}
                                >
                                    <svg
                                        className="icon-supervisor-account-outline margin-1-r"
                                        width="16"
                                        height="16"
                                    >
                                        <use xlinkHref="#icon-supervisor-account-outline" />
                                    </svg>
                                    <span>{obj.type}</span>
                                </span>
                            </p>
                        </div>
                    </li>))}
        </ul>
    )

    const renderNoPairs = () => (
        <Alert
            dismissible={false}
            icon="icon-people"
            iconClassName="ratio-square round flex-justify-content-center flex-align-items-center"
            role="status"
            fluid
        >
            <h3 className="margin-1-b body-4 font-weight-medium">
                We're finding you {isMember
                    ? 'expert advisors.'
                    : 'entrepreneurs'}...
            </h3>
            <p
                className="body-6 font-weight-book"
                style={{ color: getCssVar('--slate') }}
            >
                Check back soon to connect with your matches.
            </p>
        </Alert>
    )

    const renderPairs = () => {
        if (pairs === null)
            return null

        if (!pairs.length)
            return renderNoPairs()

        return isMember
            ? renderMemberPairs()
            : renderAdvisorPairs()
    }

    const renderEbpcApplication = () => {
        if (!isMember
                || !ebpcApplication
                || (!ebpcApplication.submitted_at
                    && !ebpcApplicationsOpen))
            return null

        const isCreated = ebpcApplication.created_at

        const isSubmitted = ebpcApplication.submitted_at

        let alertHeading = 'Start your application'

        let alertCta = 'Apply'

        let alertText = isCreated || isSubmitted
            ? `Review your ${
                !isSubmitted
                    ? 'in-progress or '
                    : ''}completed application${
                ebpcApplicationsOpen
                    ? ', and make sure to submit before applications close.'
                    : '.'}`
            : 'Join ventures from around the world who are addressing seminal issues in worldwide education – from early childhood learning to AI-powered tutoring, and everything in between – and compete to win tens of thousands of dollars in cash and prizes.'

        if (ebpcApplicationsEndDateStr
                && ebpcApplicationsOpen)
            alertText += ` Application deadline: ${ebpcApplicationsEndDateStr}.`

        if (isCreated && !isSubmitted) {
            alertHeading = 'Your application is in progress.'
            alertCta = 'Resume my application'
        }

        if (isSubmitted) {
            alertHeading = 'Thank you for applying.'
            alertCta = 'View my application'
        }

        return (
            <div className="col col-12-lg-down col-12-xl-up">
                <div className="dashboard-home-network-ebpc-application card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                    <h2 className="heading-5 margin-3-b">
                        {`${isSubmitted
                            ? 'You\'ve applied to the '
                            : ''}Milken-Penn GSE Education Business Plan Competition`}
                    </h2>

                    <Alert
                        dismissible={false}
                        icon={
                            <Image
                                src={`/images/illustration-${!isCreated
                                    ? 'lightbulb'
                                    : 'handshake'}.svg`}
                                alt=""
                                data-object-fit="cover"
                            />
                        }
                        iconClassName="ratio-square round flex-justify-content-center flex-align-items-center"
                        role="status"
                        fluid
                    >
                        <h3 className="margin-1-b body-4 font-weight-medium">
                            {alertHeading}
                        </h3>

                        <p
                            className="margin-1-b body-6 font-weight-book"
                            style={{ color: getCssVar('--slate') }}
                        >
                            {alertText}
                        </p>

                        <div className="margin-1-b">
                            <Link
                                className="btn-outline-secondary-alt btn-sm"
                                style={{ backgroundColor: getCssVar('--white') }}
                                to="dashboard/ebpc/application"
                            >
                                <span>{alertCta}</span>
                            </Link>
                        </div>

                        <a
                            className="body-5 display-inline-flex text-decoration-underline"
                            href={window.location.host === 'catapult.gse.upenn.edu'
                                ? 'https://www.educationcompetition.org/'
                                : 'https://ebpc-stage.bdf.technology/'}
                            rel="noopener noreferrer"
                            target="_blank"
                            title="Opens in a new tab"
                            style={{ color: getCssVar('--slate') }}
                        >
                            <span
                                className="font-weight-book margin-1-r"
                            >
                                Learn about the competition
                            </span>
                            <span className="flex-none">
                                <svg
                                    className="icon-launch"
                                    width="14"
                                    height="14"
                                >
                                    <use xlinkHref="#icon-launch" />
                                </svg>
                            </span>
                        </a>
                    </Alert>
                </div>
            </div>
        )
    }

    const renderCardBorderTop = (_props = {
        heading: null,
        text: null,
        Cta: null
    }) => {
        const {
            heading,
            text,
            Cta
        } = _props

        if (!heading
                && !text)
            return null

        return (
            <div className="col col-12-lg-down col-12-xl-up">
                <div className="dashboard-home-aside-card-border-t card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                    {heading
                        && <h2 className="heading-5">{heading}</h2>}
                    {text
                        && (
                            <p
                                className="body-5 pad-1-t"
                                style={{
                                    color: getCssVar('--slate')
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: text
                                }}
                            />)}
                    {Cta
                        && (
                            <div className="margin-t">
                                <Cta />
                            </div>)}
                </div>
            </div>
        )
    }

    let advancedMembershipCardProps = {
        heading: 'Accelerate your venture’s mission with Advanced Membership',
        text: `Gain access to an assessment, goal-setting, and advising
            that's helped over 400 ventures like yours advance their
            mission. Advanced membership is a lifetime benefit,
            with a one-time fee of ${advancedMembershipPrice}.`,
        Cta: () => (
            <button
                className="btn btn-outline btn-sm"
                style={{
                    color: getCssVar('--navy'),
                    borderColor: getCssVar('--cadet')
                }}
                onClick={() => history
                    .push(`${history.location.pathname}${
                        history.location.search
                            ? '&'
                            : '?'}advanced-membership-checkout=onboard`)}
            >
                <span
                    style={{
                        fontWeight: 400
                    }}
                >
                    Become an Advanced Member
                </span>
            </button>
        )
    }

    if (!isMember
            || advancedMembership === null
            || advancedMembership
            || advancedMembershipAt
            || !advancedMembershipPrice)
        advancedMembershipCardProps = {}

    return (
        <div className="dashboard-home-network">
            <div className="row-condensed">
                {renderEbpcApplication()}
                {renderCardBorderTop(advancedMembershipCardProps)}
                {isProfileApproved
                    && (!isMember
                        || (isMember
                            && Boolean((pairs || []).length)))
                    && (
                        <div className="col col-12-lg-down col-12-xl-up">
                            <div className="dashboard-home-network-pairs card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                                <h2
                                    className="heading-5 margin-3-b"
                                >
                                    {isMember
                                        ? 'Advisors'
                                        : 'Entrepreneurs'}
                                </h2>
                                {renderPairs()}
                            </div>
                        </div>)}
                {advancedMembership
                    && (
                        <div className="col col-12-lg-down col-12-xl-up">
                            <div className="dashboard-home-network-contacts card card-offscreen-x-lg-down margin-1-b">
                                <h2 className="heading-5 margin-3-b">Catapult Contacts</h2>
                                <ul className="dashboard-home-network-list dashboard-home-network-contacts-list">
                                    <li className="dashboard-home-network-list-item row flex-nowrap">
                                        <div className="col-auto pad-0-r">
                                            <div className="avatar">
                                                <Image
                                                    src="/images/headshot-gamba.png"
                                                    alt="Headshot of John Gamba"
                                                    width="40"
                                                    height="40"
                                                    data-object-fit="cover"
                                                    loading="lazy"
                                                />
                                            </div>
                                        </div>
                                        <div className="col body-6 font-weight-book">
                                            <h3 className="body-4 font-weight-medium display">
                                                John Gamba
                                            </h3>
                                            <p>
                                                <span className="display-flex">
                                                    <svg
                                                        className="icon-work-outline margin-1-r"
                                                        width="16"
                                                        height="16"
                                                    >
                                                        <use xlinkHref="#icon-work-outline" />
                                                    </svg>
                                                    <span>Entrepreneur in Residence, Catalyst @ Penn GSE</span>
                                                </span>
                                                <span className="display-flex">
                                                    <a
                                                        className="display-inline-flex"
                                                        href="mailto:jgamba@upenn.edu"
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                        title="Opens in default email client"
                                                    >
                                                        <span className="flex-none margin-1-r">
                                                            <svg
                                                                className="icon-mail-outline"
                                                                width="16"
                                                                height="16"
                                                            >
                                                                <use xlinkHref="#icon-mail-outline" />
                                                            </svg>
                                                        </span>
                                                        <span className="font-weight-book">jgamba@upenn.edu</span>
                                                    </a>
                                                </span>
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>)}
            </div>
        </div>
    )
}